import React, {useEffect, useState} from 'react'
import {version} from '@/../package.json'
import Select from '@components/select'
import * as api from '@api'
import useToast from '@/hooks/useToast'
import {useCompaniesToSwitch} from '@state/companies-to-switch'
import ErrorToast from '@components/ErrorToast'
import useTranslator from '@/hooks/useTranslator'
import {useNavigate} from 'react-router-dom'
import {useAuth} from '@state/auth'
import useFeature from '@/hooks/useFeature'
import {useWorkflowTraceTotal} from '@/views/cascade-tracing/partials/drawers/actions/tracing-ai-usage-actions'

import {Avatar, Card, Flex, NewBox as Box, Text} from '@retracedgmbh/ui-components-lib'
import {BASE_PATH} from '@constants'

const TracingCreditsPopover = ({balance, consumed, reserved, available, getTranslation}) => {
  const rows = [
    {label: getTranslation('navbar.profileDropdown.balance'), value: balance, color: null},
    {label: getTranslation('navbar.profileDropdown.consumed'), value: consumed, color: null},
    {label: getTranslation('navbar.profileDropdown.reserved'), value: reserved, color: null},
    {
      label: getTranslation('navbar.profileDropdown.creditsAvailable'),
      value: available,
      color: available < 0 ? 'tw:text-content-negative' : 'tw:text-content-positive',
    },
  ]

  return (
    <Box padding="lg" className="tw:min-w-[200px]">
      <Flex direction="column" gap="sm">
        {rows.map(({label, value, color}) => (
          <Flex key={label} justify="between" alignItems="center">
            <Text as="span" size="small" color="secondary">{label}</Text>
            <Text as="span" size="small" weight="semibold" className={color ?? ''}>{value}</Text>
          </Flex>
        ))}
      </Flex>
    </Box>
  )
}

const CreditsRow = ({balance, consumed, reserved, available, getTranslation}) => {
  const [hovered, setHovered] = useState(false)
  const total = balance
  const progressPct = total > 0 ? Math.min(100, Math.round((consumed / total) * 100)) : 0

  return (
    <Box
      position="relative"
      paddingX="md"
      paddingY="sm"
      className="tw:cursor-default"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Flex alignItems="center" justify="between" className="tw:mb-1.5">
        <Flex alignItems="center" gap="xs">
          <Text as="span" size="small" weight="semibold">
            {getTranslation('navbar.profileDropdown.tracingAiCredits')}
          </Text>
          <i className="fa-light fa-circle-info tw:text-xs tw:text-gray-400" />
        </Flex>
        <Text as="span" size="small" color="secondary">
          {getTranslation('navbar.profileDropdown.creditsLeft', {count: available})}
        </Text>
      </Flex>
      <Box className="tw:h-1.5 tw:rounded-full tw:bg-background-secondary tw:overflow-hidden">
        <Box
          className="tw:h-full tw:rounded-full tw:bg-ai tw:transition-all"
          style={{width: `${progressPct}%`}}
        />
      </Box>

      {hovered && (
        <Card className="tw:absolute tw:top-0 tw:right-full tw:mr-2 tw:z-50 tw:shadow-lg tw:min-w-[200px]">
          <TracingCreditsPopover
            balance={balance}
            consumed={consumed}
            reserved={reserved}
            available={available}
            getTranslation={getTranslation}
          />
        </Card>
      )}
    </Box>
  )
}

const ProfileDropdown = ({onHideDropdown, isImpersonating, isAuditing, isAuditor}) => {
  const {toast} = useToast()
  const {getTranslation} = useTranslator({page: 'navbar'})
  const navigate = useNavigate()
  const {isTracingAiEnabled} = useFeature()
  const {data: creditsData} = useWorkflowTraceTotal({enabled: isTracingAiEnabled && !isAuditor && !isAuditing})

  const {companiesToSwitch, setCompaniesToSwitch} = useCompaniesToSwitch(state => ({
    companiesToSwitch: state.companiesToSwitch,
    setCompaniesToSwitch: state.setCompaniesToSwitch,
  }))

  const {stopImpersonate, stopAuditing, companySwitch, me, meReceived, isAdmin} = useAuth(state => ({
    stopImpersonate: state.stopImpersonate,
    stopAuditing: state.stopAuditing,
    companySwitch: state.companySwitch,
    me: state.me,
    meReceived: state.meReceived,
    isAdmin: state.isAdmin,
  }))

  useEffect(() => {
    api.companiesListToSwitchGet().then(({data, error, status}) => {
      if (status >= 400) {
        return toast.custom(t => <ErrorToast t={t} error={error} />)
      }
      setCompaniesToSwitch(data)
    })
  }, [me?.companyId])

  const handleCompanySelection = async company => {
    companySwitch(companiesToSwitch.find(item => item.companyId === company.key))
    await api.meGet().then(response => {
      if (response.status === 200) {
        meReceived(response.data)
      }
    })

    isAdmin() ? navigate('/management/impersonate') : navigate(`/`)
    window.location.reload()
  }

  const handlerStopAuditing = () => {
    stopAuditing()
    api.meGet().then(response => {
      if (response.status === 200) {
        meReceived(response.data)
        navigate('/', {replace: true})
      }
    })
  }

  return (
    <div role={'profile-dropdown'}>
      <p className="help tw:px-4 tw:pt-0 tw:pb-1">{getTranslation('navbar.profileDropdown.account')}</p>
      <hr className=" tw:m-0 navbar-divider" />
      {!isAuditor && (
        <>
          <a
            className="navbar-item tw:font-bold "
            onClick={() => {
              onHideDropdown()
              const url = isAdmin()
                ? `${BASE_PATH.COMPANY_MANAGEMENT}/${me.company.id}/view`
                : isAuditing
                  ? `/auditor/companies/${me.company.id}/view`
                  : `/companies/${me?.company?.id}/view`
              navigate(url)
              window.location.reload()
            }}
          >
            <Avatar
              size="sm"
              src={
                me?.company?.featuredImageFileUrl ||
                `${import.meta.env.VITE_CDN_URI}/static/dashboard-service/image-404.jpg`
              }
            />
            <span
              className="tw:ml-2 ellipsis"
              role={'my-company-name-span'}
              style={{maxWidth: '130px', whiteSpace: 'normal'}}
            >
              {me?.company?.name}
            </span>
          </a>
          <hr className=" tw:m-0 navbar-divider" />
        </>
      )}
      {!isAuditor && !isAuditing && (
        <>
          <a
            className="navbar-item tw:font-bold tw:py-1"
            onClick={() => {
              onHideDropdown()
              navigate(isAdmin() ? '/management/profile/edit' : '/profile/view')
            }}
          >
            <Avatar
              size="sm"
              src={me?.avatarFileUrl || `${import.meta.env.VITE_CDN_URI}/static/dashboard-service/image-404.jpg`}
            />
            <span className="tw:ml-2">{getTranslation('navbar.profileDropdown.myProfile')}</span>
          </a>
          <hr className=" tw:m-0 navbar-divider" />
        </>
      )}

      {isTracingAiEnabled && !isAuditor && !isAuditing && creditsData && (
        <>
          <CreditsRow
            balance={creditsData.balance ?? 0}
            consumed={creditsData.consumed ?? 0}
            reserved={creditsData.reserved ?? 0}
            available={(creditsData.balance ?? 0) - (creditsData.reserved ?? 0)}
            getTranslation={getTranslation}
          />
          <hr className="tw:m-0 navbar-divider" />
        </>
      )}

      {isImpersonating || isAuditing ? (
        <>
          {isImpersonating && (
            <a
              className="navbar-item tw:font-bold"
              id="profile-action-end-impersonate"
              onClick={() => {
                onHideDropdown()
                stopImpersonate()
                navigate('/management/impersonate', {replace: true})
              }}
            >
              {getTranslation('navbar.profileDropdown.endImpersonate')}
            </a>
          )}
          {isAuditing && (
            <a
              className="navbar-item tw:font-bold"
              id="profile-action-end-auditing"
              onClick={() => {
                onHideDropdown()
                handlerStopAuditing()
              }}
            >
              {getTranslation('navbar.profileDropdown.endAudit')}
            </a>
          )}
        </>
      ) : (
        <a
          className="navbar-item tw:font-bold"
          id="profile-action-logout"
          onClick={() => {
            onHideDropdown()
            navigate('/logout')
          }}
        >
          <span className="icon tw:mr-1">
            <i className="fa-light fa-sign-out tw:text-base" />
          </span>
          {getTranslation('navbar.profileDropdown.logout')}
        </a>
      )}
      <hr className="tw:m-0 navbar-divider" />

      {companiesToSwitch.length > 0 && !isAuditing && (
        <Select
          controlName="company"
          role={'select-company-btn'}
          placeholder={getTranslation('navbar.profileDropdown.companiesToSwitch.select.placeholder')}
          width="100%"
          options={companiesToSwitch.map(item => ({
            text: `${item.name || ''}`,
            key: item.companyId,
            role: item.companyId,
          }))}
          onChange={handleCompanySelection}
        />
      )}
      <hr className="tw:m-0 navbar-divider" />
      <p className="help tw:px-4 tw:pt-1 tw:text-gray-light">v{version}</p>
    </div>
  )
}
export default ProfileDropdown
